// react
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTable from "react-table";
import moment from "moment";

// @material-ui/icons
import ListAlt from "@material-ui/icons/ListAlt";
import Lock from "@material-ui/icons/Lock";

// @material-ui components
import CircularProgress from "@material-ui/core/CircularProgress";

// core components
import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import CustomLinearProgress from "beaktShared/components/CustomLinearProgress/CustomLinearProgress.js";
import Snackbar from "beaktShared/components/Snackbar/Snackbar.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";
import TipoComunicadoBadge from "beaktShared/components/Comunicado/TipoComunicadoBadge";

// services
import * as ComunicadoService from "./ComunicadoService";
import { formateDate, formateDateandTime } from "beaktShared/helpers/masks";
import { noCaseSensitive } from 'beaktShared/helpers/extras';

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/crudeStyle.js";
import { successColor, warningColor, dangerColor, grayColor, infoColor } from "assets/jss/material-dashboard-pro-react.js";

const { SituacaoComunicado, TipoEscopoComunicado, SituacaoAdesaoComunicado, SituacaoPrazoAceite } = ComunicadoService;

const badge = (color) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    backgroundColor: color,
    whiteSpace: "nowrap"
});

const useStyles = makeStyles({
    ...styles,
    badgeAceito: badge(successColor[0]),
    badgePendente: badge(warningColor[0]),
    badgeVencido: badge(dangerColor[0]),
    badgeNeutro: badge(grayColor[0]),
    badgeInfo: badge(infoColor[0]),
    info: {
        color: "#555",
        fontSize: "13px",
        marginBottom: "12px",
        "& > span": {
            display: "inline-block",
            marginRight: "20px",
            marginBottom: "6px"
        }
    },
    totais: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: "16px",
        "& > div": {
            marginRight: "32px",
            marginBottom: "8px"
        }
    },
    totalValor: {
        fontSize: "22px",
        fontWeight: 500,
        display: "block"
    },
    totalLabel: {
        fontSize: "12px",
        color: "#777"
    },
    adesao: {
        minWidth: "220px",
        flex: 1
    },
    textCenter: {
        textAlign: "center"
    }
});

// Props: comunicado (linha da listagem), handleView() para voltar
export default function ComunicadoLogAceite({ comunicado, handleView }) {
    const { t } = useTranslation();
    const classes = useStyles();

    const [notification, setNotification] = useState({ show: false, color: 'info', message: '' });
    const showNotification = (message, color) => {
        setNotification({ show: true, color: color, message: message });
        setTimeout(() => setNotification({ show: false }), 6000);
    };

    const [log, setLog] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isExportando, setIsExportando] = useState(false);

    const carregar = () => {
        setIsLoading(true);
        setIsError(false);
        ComunicadoService.obterLogAceites(comunicado.comunicadoId).then(result => {
            setIsLoading(false);
            if (!result.ok || !result.data) {
                setIsError(true);
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            setLog(result.data);
        });
    };

    useEffect(() => {
        carregar();
    // eslint-disable-next-line
    }, [comunicado.comunicadoId]);

    const handleExportarCsv = () => {
        setIsExportando(true);
        ComunicadoService.exportarLogAceitesCsv(comunicado.comunicadoId).then(result => {
            setIsExportando(false);
            if (!result.ok) {
                showNotification(result.message, 'danger');
                return;
            }
            // Nome gerado no cliente: o Content-Disposition pode nao ser exposto pelo CORS
            const nomeArquivo = "LogAceites_Comunicado_" + comunicado.comunicadoId + "_" + moment().format("YYYYMMDDHHmmss") + ".csv";
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = nomeArquivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        });
    };

    // ************************** RENDER HELPERS **************************
    const renderSituacaoComunicado = (valor) => {
        switch (valor) {
            case SituacaoComunicado.Ativo:
                return <span className={classes.badgeAceito}>{t('label.active')}</span>;
            case SituacaoComunicado.Inativo:
                return <span className={classes.badgeNeutro}>{t('label.inactive')}</span>;
            case SituacaoComunicado.Pendente:
                return <span className={classes.badgePendente}>{t('notice_board.situation_draft')}</span>;
            default:
                return null;
        }
    };

    const renderSituacaoAdesao = (valor) => valor === SituacaoAdesaoComunicado.Aceito
        ? <span className={classes.badgeAceito}>{t('notice_board.accepted')}</span>
        : <span className={classes.badgePendente}>{t('notice_board.filter_pending')}</span>;

    const labelSituacaoPrazo = (valor) => {
        switch (valor) {
            case SituacaoPrazoAceite.SemPrazo: return t('notice_board.deadline_status_none');
            case SituacaoPrazoAceite.DentroDoPrazo: return t('notice_board.deadline_status_on_time');
            case SituacaoPrazoAceite.AceitoAposPrazo: return t('notice_board.deadline_status_late');
            case SituacaoPrazoAceite.PrazoVencido: return t('notice_board.deadline_status_expired');
            default: return "-";
        }
    };

    const renderSituacaoPrazo = (valor) => {
        const label = labelSituacaoPrazo(valor);
        switch (valor) {
            case SituacaoPrazoAceite.DentroDoPrazo:
                return <span className={classes.badgeAceito}>{label}</span>;
            case SituacaoPrazoAceite.AceitoAposPrazo:
                return <span className={classes.badgePendente}>{label}</span>;
            case SituacaoPrazoAceite.PrazoVencido:
                return <span className={classes.badgeVencido}>{label}</span>;
            default:
                return <span className={classes.badgeNeutro}>{label}</span>;
        }
    };

    const columns = [
        {
            Header: t('notice_board.user'),
            accessor: "nomeUsuario"
        },
        {
            Header: t('notice_board.login'),
            accessor: "login",
            width: 140
        },
        {
            Header: t('notice_board.unit'),
            accessor: "unidade"
        },
        {
            Header: t('notice_board.situation'),
            accessor: "situacao",
            Cell: (props) => renderSituacaoAdesao(props.value),
            filterable: false,
            width: 110
        },
        {
            Header: t('notice_board.acceptance_date'),
            accessor: "dataHoraAceite",
            Cell: (props) => <span>{props.value ? formateDateandTime(props.value) : "-"}</span>,
            filterable: false,
            width: 150
        },
        {
            Header: t('notice_board.deadline_status'),
            accessor: "situacaoPrazo",
            Cell: (props) => renderSituacaoPrazo(props.value),
            filterable: false,
            width: 160
        },
        {
            Header: t('notice_board.inactivated_no_acceptance'),
            accessor: "inativadoPorFaltaDeAceite",
            Cell: (props) => props.value
                ? <span className={classes.badgeVencido}>
                    {t('label.yes')}{props.original.dataInativacao ? " · " + formateDate(props.original.dataInativacao) : ""}
                  </span>
                : <span>{t('label.not')}</span>,
            filterable: false,
            width: 150
        }
    ];

    const renderResumo = () => {
        const total = log.totalUsuarios || 0;
        const aceites = log.totalAceites || 0;
        const adesao = ComunicadoService.calcularAdesao(aceites, total);

        return (
            <div>
                <div className={classes.info}>
                    <span><TipoComunicadoBadge tipo={log.tipo} /></span>
                    <span>{renderSituacaoComunicado(log.situacao)}</span>
                    <span>
                        <b>{t('notice_board.recipients')}:</b>{" "}
                        {log.tipoEscopo === TipoEscopoComunicado.Matriz ? t('notice_board.scope_matriz') : t('notice_board.scope_establishment')}
                    </span>
                    <span>
                        <b>{t('notice_board.deadline')}:</b>{" "}
                        {log.dataLimiteAceite ? formateDateandTime(log.dataLimiteAceite) : t('notice_board.no_deadline')}
                    </span>
                </div>

                <div className={classes.totais}>
                    <div>
                        <span className={classes.totalValor}>{total}</span>
                        <span className={classes.totalLabel}>{t('notice_board.total_users')}</span>
                    </div>
                    <div>
                        <span className={classes.totalValor} style={{ color: successColor[0] }}>{aceites}</span>
                        <span className={classes.totalLabel}>{t('notice_board.total_accepted')}</span>
                    </div>
                    <div>
                        <span className={classes.totalValor} style={{ color: warningColor[0] }}>{log.totalPendentes || 0}</span>
                        <span className={classes.totalLabel}>{t('notice_board.total_pending')}</span>
                    </div>
                    <div className={classes.adesao}>
                        <CustomLinearProgress
                            variant="determinate"
                            color={adesao.completo ? "success" : "info"}
                            value={adesao.percentual}
                            style={{ marginBottom: "4px" }}
                        />
                        <span className={classes.totalLabel}>{t('notice_board.adhesion')}: {adesao.texto}</span>
                    </div>
                </div>

                <SnackbarContent
                    color="info"
                    icon={Lock}
                    message={t('notice_board.immutable_log_warning')}
                />
            </div>
        );
    };

    const renderConteudo = () => {
        if (isLoading) {
            return (
                <div className={classes.textCenter}>
                    <CircularProgress />
                    <p>{t('label.loading')}...</p>
                </div>
            );
        }

        if (isError || !log) {
            return (
                <div className={classes.textCenter}>
                    <p>{t('notice_board.load_log_error')}</p>
                    <Button color="primary" onClick={carregar}>{t('notice_board.try_again')}</Button>
                </div>
            );
        }

        return (
            <div>
                {renderResumo()}
                <ReactTable
                    data={log.itens || []}
                    filterable
                    defaultFilterMethod={noCaseSensitive}
                    columns={columns}
                    defaultPageSize={10}
                    minRows={5}
                    showPaginationBottom={true}
                    className="-striped -highlight"
                    previousText={t('button.previous')}
                    nextText={t('button.next')}
                    loadingText={t('label.loading') + "..."}
                    noDataText={t('information_message.no_records_found') + "!"}
                    pageText={t('label.page')}
                    ofText={t('label.of')}
                    rowsText={t('label.rows')}
                />
            </div>
        );
    };

    return (
        <div>
            {notification.show &&
                <Snackbar
                    place="tr"
                    color={notification.color}
                    message={notification.message}
                    open={notification.show}
                    closeNotification={() => setNotification({ show: false })}
                    close
                />}

            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <ListAlt />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>
                                {t('notice_board.read_log') + ": " + comunicado.titulo}
                            </h4>
                        </CardHeader>
                        <CardBody>
                            {renderConteudo()}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            <GridContainer justify="center">
                <GridItem xs={12} className={classes.textCenter}>
                    <Button color="transparent" onClick={() => handleView()}>
                        {t('notice_board.back')}
                    </Button>
                    <Button color="primary" disabled={isLoading || isError || isExportando} onClick={handleExportarCsv}>
                        {isExportando ? t('label.loading') + "..." : t('notice_board.export_csv')}
                    </Button>
                </GridItem>
            </GridContainer>
        </div>
    );
}
